import { type ElementType } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";

interface RevealHeadingProps {
  /** Main heading text (rendered in the inherited color). */
  text: string;
  /** Optional trailing portion rendered in the accent color. */
  accent?: string;
  /** Tailwind class for the accent words. */
  accentClassName?: string;
  className?: string;
  /** Heading tag to render (default h2). */
  as?: ElementType;
}

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};

const wordVariant: Variants = {
  hidden: { opacity: 0, y: "0.4em", filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: "0em",
    filter: "blur(0px)",
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

/**
 * Cinematic heading that reveals word-by-word with a soft blur/rise the first
 * time it scrolls into view. Falls back to a plain heading when the user
 * prefers reduced motion.
 */
export function RevealHeading({
  text,
  accent,
  accentClassName = "text-accent-500",
  className,
  as: Tag = "h2",
}: RevealHeadingProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <Tag className={className}>
        {text}
        {accent ? <span className={cn("ml-[0.25em]", accentClassName)}>{accent}</span> : null}
      </Tag>
    );
  }

  const baseWords = text.split(" ");
  const accentWords = accent ? accent.split(" ") : [];

  return (
    <Tag className={className}>
      <motion.span
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.5 }}
        className="inline"
      >
        {baseWords.map((w, i) => (
          <motion.span key={`b-${i}`} variants={wordVariant} className="mr-[0.25em] inline-block">
            {w}
          </motion.span>
        ))}
        {accentWords.map((w, i) => (
          <motion.span
            key={`a-${i}`}
            variants={wordVariant}
            className={cn("mr-[0.25em] inline-block", accentClassName)}
          >
            {w}
          </motion.span>
        ))}
      </motion.span>
    </Tag>
  );
}
